import React, { MouseEvent } from 'react';
import { Edit, Eye, Trash, ClipboardPlus, PlayerSkipForward } from 'tabler-icons-react';
import { Anchor, Box, Group } from '@mantine/core';
import { useIntlLight } from '../../../../i18n';
import useGlobalModal from '../../../hooks/useGlobalModal';
import { IconTooltip } from '../../IconTooltip';
import { ICustomAction, TABLE_NAVIGATE_ACTION } from '../../../types';
import useGlobalCustomStyles from '../../../../../useGlobalCustomStyles';

export default function ActionCell({
  isUpdate,
  isView,
  isDelete,
  deleteLabel,
  deleteSecure = false,
  viewTooltip,
  handlerNavigate,
  row,
  buttons,
  customActions,
  actionGroupInlineStyle,
}: {
  isUpdate?: boolean;
  isView?: boolean;
  isDelete?: boolean;
  deleteLabel?: string | React.ReactElement;
  deleteSecure?: boolean;
  viewTooltip?: string;
  handlerNavigate: any;
  row: any;
  buttons?: {
    templateify?: {
      onClick: (declarationId: string) => void;
    };
    createDeclarationFromTemplate?: {
      onClick: (templateId: string) => void;
    };
    createDeclarationFromTemplateLot?: {
      onClick: (templateLotId: string) => void;
    };
  };
  customActions?: ICustomAction[];
  actionGroupInlineStyle?: Record<string, string>;
}) {
  const { t } = useIntlLight();
  const { classes } = useGlobalCustomStyles();
  const { open } = useGlobalModal();

  return (
    <Group position="left" {...(actionGroupInlineStyle && { sx: actionGroupInlineStyle })}>
      {isView && (
        <Anchor
          role="button"
          onClick={() =>
            handlerNavigate({ action: TABLE_NAVIGATE_ACTION.VIEW, row: row?.original })
          }
        >
          <IconTooltip label={viewTooltip || t('app.viewTooltip')} withArrow>
            <Eye size={18} className={classes.actionIconBlue} />
          </IconTooltip>
        </Anchor>
      )}
      {isUpdate && (
        <Anchor
          role="button"
          onClick={(e: any) => {
            e.stopPropagation();
            handlerNavigate({ action: TABLE_NAVIGATE_ACTION.EDIT, row: row?.original });
          }}
        >
          <IconTooltip label={t('btn_edit_toolTip')} withArrow>
            <Edit size={18} className={classes.actionIconBlue} />
          </IconTooltip>
        </Anchor>
      )}
      {buttons?.templateify && (
        <Anchor
          role="button"
          onClick={(e: MouseEvent<any>) => {
            e.preventDefault();
            e.stopPropagation();

            buttons?.templateify?.onClick(row?.original?.id);
          }}
        >
          <ClipboardPlus size={18} className={classes.actionIconBlue} />
        </Anchor>
      )}
      {buttons?.createDeclarationFromTemplate && (
        <Anchor
          role="button"
          onClick={(e: MouseEvent<any>) => {
            e.preventDefault();
            e.stopPropagation();

            buttons?.createDeclarationFromTemplate?.onClick(row?.original?.id);
          }}
        >
          <IconTooltip label={t('btn_play_toolTip')} withArrow>
            <PlayerSkipForward size={18} className={classes.actionIconBlue} />
          </IconTooltip>
        </Anchor>
      )}
      {buttons?.createDeclarationFromTemplateLot && (
        <Anchor
          role="button"
          onClick={(e: MouseEvent<any>) => {
            e.preventDefault();
            e.stopPropagation();

            buttons?.createDeclarationFromTemplateLot?.onClick(row?.original?.id);
          }}
        >
          <IconTooltip label={t('btn_play_toolTip')} withArrow>
            <PlayerSkipForward size={18} className={classes.actionIconBlue} />
          </IconTooltip>
        </Anchor>
      )}
      {customActions?.map(
        (item: ICustomAction, index: number) =>
          !item.isDisabled && (
            <Anchor
              key={`${index.toString()}_button`}
              onClick={(e: MouseEvent<any>) => {
                e.preventDefault();
                e.stopPropagation();
                if (item.confirmationLabel) {
                  open?.(null, {
                    title: item.confirmationLabel,
                    onConfirm: () => item.callBack(),
                  });
                } else {
                  item.callBack();
                }
              }}
            >
              <IconTooltip label={item.toolTipLabel} withArrow>
                <Box
                  className={
                    React.isValidElement(item.icon) && item.icon.type === Trash
                      ? classes.actionIconRed
                      : classes.actionIconBlue
                  }
                >
                  {item.icon}
                </Box>
              </IconTooltip>
            </Anchor>
          )
      )}
      {isDelete && (
        <Anchor
          role="button"
          onClick={(event: any) => {
            event.stopPropagation();
            open?.(deleteLabel || t('deletionGenericMessage'), {
              title: t('deletionGenericMessage'),
              secureConfirm: deleteSecure,
              onConfirm: () =>
                handlerNavigate({ action: TABLE_NAVIGATE_ACTION.DELETE, row: row?.original }),
            });
          }}
        >
          <IconTooltip label={t('app.delete')} withArrow>
            <Trash size={18} className={classes.actionIconRed} />
          </IconTooltip>
        </Anchor>
      )}
    </Group>
  );
}
